import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TableProps } from 'antd';
import {
    Button, Empty, Flex, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Spin, Switch, Table, Tag, Tooltip,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { WithAuth } from '../../components/with-auth';
import MainWrapper from '../../layout/MainWrapper';
import { createAxiosInstance } from '../../api/ApiRequest';
import { useServerContext } from '../../context/ServerContext';
import { useAuth } from '../../context/AuthContext';
import { BOT_TYPE, BotType } from '../../constants/botType';
import { BotServerPublic, Pm2ServiceLogPublic } from '../../types/botServer';
import { getServerTypeLabel, getServerTypeTagColor, isMainBot, isWlBot } from '../../utils/serverTypeLabels';
import { notifyError, notifySuccess } from '../../utils/toast';

interface BotServerFormValues {
    serverId: string;
    type: BotType;
    enabled: boolean;
    databaseName?: string;
    sortOrder?: number;
}

const BotServers: React.FC = () => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const axiosInstance = useMemo(() => createAxiosInstance(navigate, logout), [navigate, logout]);
    const { refreshConfig } = useServerContext();
    const [form] = Form.useForm<BotServerFormValues>();

    const [servers, setServers] = useState<BotServerPublic[]>([]);
    const [pm2Services, setPm2Services] = useState<Pm2ServiceLogPublic[]>([]);
    const [loading, setLoading] = useState(true);
    const [pm2Loading, setPm2Loading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pm2Saving, setPm2Saving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [pm2ModalOpen, setPm2ModalOpen] = useState(false);
    const [editingServerId, setEditingServerId] = useState<string | null>(null);
    const [pm2Form] = Form.useForm<{ processName: string }>();
    const mainBotCount = servers.filter((s) => isMainBot(s.type)).length;
    const wlBotCount = servers.filter((s) => isWlBot(s.type)).length;
    const existingMainBotId = servers.find((s) => isMainBot(s.type))?.serverId;

    const fetchServers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post<{
                flag: boolean;
                servers: BotServerPublic[];
                message?: string;
            }>('/api/v1/get_bot_servers');
            if (res.data?.flag) {
                setServers(res.data.servers ?? []);
            } else {
                notifyError(res.data?.message || 'Failed to load bot servers');
            }
        } catch {
            notifyError('Failed to load bot servers');
        } finally {
            setLoading(false);
        }
    }, [axiosInstance]);

    const fetchPm2Services = useCallback(async () => {
        setPm2Loading(true);
        try {
            const res = await axiosInstance.post<{
                flag: boolean;
                services: Pm2ServiceLogPublic[];
                message?: string;
            }>('/api/v1/get_pm2_service_logs');
            if (res.data?.flag) {
                setPm2Services(res.data.services ?? []);
            } else {
                notifyError(res.data?.message || 'Failed to load PM2 services');
            }
        } catch {
            notifyError('Failed to load PM2 services');
        } finally {
            setPm2Loading(false);
        }
    }, [axiosInstance]);

    useEffect(() => {
        fetchServers();
        fetchPm2Services();
    }, [fetchServers, fetchPm2Services]);

    const openCreateModal = () => {
        setEditingServerId(null);
        form.resetFields();
        form.setFieldsValue({
            type: mainBotCount > 0 ? BOT_TYPE.WL_BOT : BOT_TYPE.MAIN_BOT,
            enabled: true,
            sortOrder: servers.length,
        });
        setModalOpen(true);
    };

    const openEditModal = (record: BotServerPublic) => {
        setEditingServerId(record.serverId);
        form.setFieldsValue({
            serverId: record.serverId,
            type: record.type,
            enabled: record.enabled,
            sortOrder: record.sortOrder,
            databaseName: record.databaseName,
        });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingServerId(null);
        form.resetFields();
    };

    const handleSave = async (values: BotServerFormValues) => {
        setSaving(true);
        try {
            const payload: Record<string, unknown> = {
                serverId: values.serverId.trim(),
                type: values.type,
                enabled: values.enabled,
                sortOrder: values.sortOrder ?? 0,
            };

            if (values.databaseName?.trim()) {
                payload.databaseName = values.databaseName.trim();
            }

            const res = await axiosInstance.post<{ flag: boolean; message?: string }>(
                '/api/v1/save_bot_server',
                payload
            );

            if (res.data?.flag) {
                notifySuccess(editingServerId ? 'Bot server updated' : 'Bot server created');
                closeModal();
                await fetchServers();
                await refreshConfig(true);
            } else {
                notifyError(res.data?.message || 'Save failed');
            }
        } catch {
            notifyError('Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (serverId: string) => {
        try {
            const res = await axiosInstance.post<{ flag: boolean; message?: string }>(
                '/api/v1/delete_bot_server',
                { serverId }
            );
            if (res.data?.flag) {
                notifySuccess('Bot server deleted');
                await fetchServers();
                await refreshConfig(true);
            } else {
                notifyError(res.data?.message || 'Delete failed');
            }
        } catch {
            notifyError('Delete failed');
        }
    };

    const handleDeletePm2Service = async (processName: string) => {
        try {
            const res = await axiosInstance.post<{ flag: boolean; message?: string }>(
                '/api/v1/delete_pm2_service_log',
                { processName }
            );
            if (res.data?.flag) {
                notifySuccess('PM2 service removed');
                await fetchPm2Services();
            } else {
                notifyError(res.data?.message || 'Delete failed');
            }
        } catch {
            notifyError('Delete failed');
        }
    };

    const handleCreatePm2Service = async (values: { processName: string }) => {
        setPm2Saving(true);
        try {
            const res = await axiosInstance.post<{ flag: boolean; message?: string }>(
                '/api/v1/create_pm2_service_log',
                { processName: values.processName.trim(), sortOrder: pm2Services.length }
            );
            if (res.data?.flag) {
                notifySuccess('PM2 service added');
                setPm2ModalOpen(false);
                pm2Form.resetFields();
                await fetchPm2Services();
            } else {
                notifyError(res.data?.message || 'Create failed');
            }
        } catch {
            notifyError('Create failed');
        } finally {
            setPm2Saving(false);
        }
    };

    const pm2Columns: TableProps<Pm2ServiceLogPublic>['columns'] = [
        {
            title: 'Service Name',
            dataIndex: 'processName',
            key: 'processName',
            render: (processName: string) => <span className="bot-server-id">{processName}</span>,
        },
        {
            title: 'Type',
            key: 'type',
            width: '12%',
            render: () => <Tag color="default">Non-bot</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '10%',
            align: 'right',
            render: (_: unknown, record: Pm2ServiceLogPublic) => (
                <Popconfirm
                    title="Remove this PM2 service?"
                    description={`Stop monitoring logs for "${record.processName}".`}
                    onConfirm={() => handleDeletePm2Service(record.processName)}
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                >
                    <Tooltip title="Remove">
                        <Button
                            type="text"
                            size="small"
                            danger
                            className="bot-server-action-btn"
                            icon={<DeleteOutlined />}
                        />
                    </Tooltip>
                </Popconfirm>
            ),
        },
    ];

    const columns: TableProps<BotServerPublic>['columns'] = [
        {
            title: 'Server',
            dataIndex: 'serverId',
            key: 'serverId',
            width: '16%',
            render: (serverId: string) => <span className="bot-server-id">{serverId}</span>,
        },
        {
            title: 'Database',
            dataIndex: 'databaseName',
            key: 'databaseName',
            width: '22%',
            render: (databaseName: string) => (
                <span className="bot-server-database">{databaseName || '—'}</span>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: '12%',
            render: (t: BotType) => (
                <Tag className="bot-server-type-tag" color={getServerTypeTagColor(t)}>
                    {getServerTypeLabel(t)}
                </Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'enabled',
            key: 'enabled',
            width: '12%',
            render: (v: boolean) => (
                <Tag className="bot-server-status-tag" color={v ? 'success' : 'default'}>
                    {v ? 'Enabled' : 'Disabled'}
                </Tag>
            ),
        },
        {
            title: 'Sort',
            dataIndex: 'sortOrder',
            key: 'sortOrder',
            width: '8%',
            align: 'center',
            render: (sortOrder: number) => <span className="bot-server-sort">{sortOrder}</span>,
        },
        {
            title: 'Config',
            key: 'config',
            width: '20%',
            render: (_: unknown, record: BotServerPublic) => {
                const tags = [
                    record.mongoConfigured && { key: 'mongo', label: 'Mongo', color: 'geekblue' as const },
                    record.encryptionConfigured && { key: 'encryption', label: 'Encryption', color: 'gold' as const },
                ].filter(Boolean) as { key: string; label: string; color: string }[];

                if (!tags.length) {
                    return <span className="bot-server-config-empty">Not configured</span>;
                }

                return (
                    <Space size={6} wrap className="bot-server-config-tags">
                        {tags.map((tag) => (
                            <Tag key={tag.key} color={tag.color}>{tag.label}</Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '10%',
            align: 'right',
            render: (_: unknown, record: BotServerPublic) => (
                <Space size={4} className="bot-server-actions">
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            size="small"
                            className="bot-server-action-btn"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete this bot server?"
                        description={`Remove "${record.serverId}" from the platform.`}
                        onConfirm={() => handleDelete(record.serverId)}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                    >
                        <Tooltip title="Delete">
                            <Button
                                type="text"
                                size="small"
                                danger
                                className="bot-server-action-btn"
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <WithAuth>
            <MainWrapper>
                <Flex align="center" justify="space-between" style={{ marginBottom: 28, width: '100%' }}>
                    <Flex align="center" wrap="wrap" gap={12}>
                        <span className="dashboard-table-title">Bot Servers</span>
                        <Tag color="blue">Main-bot: {mainBotCount}/1</Tag>
                        <Tag color="purple">WL-bot: {wlBotCount}</Tag>
                    </Flex>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                        Add Server
                    </Button>
                </Flex>

                {loading ? (
                    <div className="bot-servers-loading">
                        <Spin />
                    </div>
                ) : servers.length === 0 ? (
                    <Empty
                        className="bot-servers-empty"
                        description="No bot servers configured yet"
                    >
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                            Add Server
                        </Button>
                    </Empty>
                ) : (
                    <Table
                        className="dashboard-data-table bot-servers-table"
                        rowKey="serverId"
                        columns={columns}
                        dataSource={servers}
                        pagination={false}
                        bordered
                        size="middle"
                        tableLayout="fixed"
                    />
                )}

                <Flex align="center" justify="space-between" style={{ marginTop: 48, marginBottom: 28, width: '100%' }}>
                    <Flex align="center" wrap="wrap" gap={12}>
                        <span className="dashboard-table-title">Non-bots Servers</span>
                        <Tag>Non-bot: {pm2Services.length}</Tag>
                    </Flex>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setPm2ModalOpen(true)}>
                        Add Non-Bot
                    </Button>
                </Flex>

                {pm2Loading ? (
                    <div className="bot-servers-loading">
                        <Spin />
                    </div>
                ) : pm2Services.length === 0 ? (
                    <Empty
                        className="bot-servers-empty"
                        description="No non-bot PM2 services configured yet"
                    >
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setPm2ModalOpen(true)}>
                            Add Non-Bot
                        </Button>
                    </Empty>
                ) : (
                    <Table
                        className="dashboard-data-table bot-servers-table"
                        rowKey="processName"
                        columns={pm2Columns}
                        dataSource={pm2Services}
                        pagination={false}
                        bordered
                        size="middle"
                        tableLayout="fixed"
                    />
                )}

                <Modal
                    title="Add Non-Bot"
                    open={pm2ModalOpen}
                    onCancel={() => {
                        setPm2ModalOpen(false);
                        pm2Form.resetFields();
                    }}
                    footer={null}
                    destroyOnClose
                >
                    <Form
                        form={pm2Form}
                        layout="vertical"
                        onFinish={handleCreatePm2Service}
                        autoComplete="off"
                    >
                        <Form.Item
                            label="Service Name"
                            name="processName"
                            rules={[
                                { required: true, message: 'Service Name is required' },
                                { pattern: /^\S+$/, message: 'Service Name cannot contain spaces' },
                            ]}
                            extra="Must match the PM2 process name. Shown on Bot Log page only, not in the server selector."
                        >
                            <Input placeholder="e.g. manage-platform, nginx-proxy" />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                            <Space>
                                <Button onClick={() => {
                                    setPm2ModalOpen(false);
                                    pm2Form.resetFields();
                                }}>
                                    Cancel
                                </Button>
                                <Button type="primary" htmlType="submit" loading={pm2Saving}>
                                    Add
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title={editingServerId ? `Edit Server: ${editingServerId}` : 'Add Bot Server'}
                    open={modalOpen}
                    onCancel={closeModal}
                    footer={null}
                    destroyOnClose
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        autoComplete="off"
                    >
                        <Form.Item
                            label="Server ID"
                            name="serverId"
                            rules={[
                                { required: true, message: 'Server ID is required' },
                                { pattern: /^\S+$/, message: 'Server ID cannot contain spaces' },
                            ]}
                            extra="Must match the PM2 process name."
                        >
                            <Input disabled={Boolean(editingServerId)} placeholder="e.g. dist, boost-legends" />
                        </Form.Item>

                        <Form.Item
                            label="Type"
                            name="type"
                            rules={[{ required: true, message: 'Type is required' }]}
                        >
                            <Select
                                options={[
                                    {
                                        value: BOT_TYPE.MAIN_BOT,
                                        label: getServerTypeLabel(BOT_TYPE.MAIN_BOT),
                                        disabled: mainBotCount > 0 && editingServerId !== existingMainBotId,
                                    },
                                    { value: BOT_TYPE.WL_BOT, label: getServerTypeLabel(BOT_TYPE.WL_BOT) },
                                ]}
                            />
                        </Form.Item>

                        <Form.Item label="Enabled" name="enabled" valuePropName="checked">
                            <Switch />
                        </Form.Item>

                        <Form.Item
                            label="Database Name"
                            name="databaseName"
                            rules={[
                                ...(editingServerId ? [] : [{ required: true, message: 'Database name is required' }]),
                                { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Use letters, numbers, underscore, or hyphen' },
                            ]}
                            extra={``}
                        >
                            <Input placeholder="e.g. us_volume_bot, boost_legend_bot" />
                        </Form.Item>

                        <Form.Item label="Sort Order" name="sortOrder">
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                            <Space>
                                <Button onClick={closeModal}>Cancel</Button>
                                <Button type="primary" htmlType="submit" loading={saving}>
                                    {editingServerId ? 'Save' : 'Create'}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </MainWrapper>
        </WithAuth>
    );
};

export default BotServers;
